// src/components/events/GiftRegistryView.jsx
import React, { useState, useEffect } from 'react';
import { Gift, ExternalLink, Heart, Check, ShoppingCart } from 'lucide-react';
import { eventsService } from '../../services/eventsService';

export const GiftRegistryView = ({ bookId }) => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, available, purchased

  useEffect(() => {
    loadGifts();
  }, [bookId]);

  const loadGifts = async () => {
    try {
      const giftsData = await eventsService.getGiftRegistry(bookId);
      setGifts(giftsData);
    } catch (error) {
      console.error('Error loading gifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGifts = gifts.filter(gift => {
    if (filter === 'available') return !gift.purchased;
    if (filter === 'purchased') return gift.purchased;
    return true;
  });

  const handlePurchaseGift = async (gift) => {
    try {
      // This would integrate with your payment system
      // For now, we'll just mark as purchased
      await eventsService.updateGiftItem(bookId, gift.id, {
        purchased: true,
        purchaseCount: gift.purchaseCount + 1
      });
      
      await loadGifts();
    } catch (error) {
      console.error('Error purchasing gift:', error);
      alert('Failed to process gift purchase');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift className="h-8 w-8 text-pink-600" />
        </div>
        <h2 className="text-3xl font-bold mb-4">Gift Registry</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Show your love and support by choosing from our carefully curated gift registry. 
          Each gift helps make this special event even more memorable.
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 rounded-lg p-1 inline-flex">
          {[
            { id: 'all', label: 'All Gifts' },
            { id: 'available', label: 'Available' },
            { id: 'purchased', label: 'Purchased' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                filter === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Gifts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGifts.map(gift => (
          <div key={gift.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
            {/* Gift Image */}
            {gift.image && (
              <div className="aspect-w-16 aspect-h-12 mb-4">
                <img
                  src={gift.image}
                  alt={gift.name}
                  className="w-full h-48 object-cover rounded-t-xl"
                />
              </div>
            )}
            
            <div className="p-6">
              {/* Gift Info */}
              <div className="mb-4">
                <h3 className="font-semibold text-lg mb-2">{gift.name}</h3>
                <p className="text-gray-600 text-sm mb-3">{gift.description}</p>
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-2xl font-bold text-emerald-600">
                    ${gift.price}
                  </span>
                  {gift.store && (
                    <span className="text-sm text-gray-500">
                      at {gift.store}
                    </span>
                  )}
                </div>

                {/* Priority Badge */}
                {gift.priority && (
                  <div className="mb-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      gift.priority === 'high' ? 'bg-red-100 text-red-700' :
                      gift.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {gift.priority} priority
                    </span>
                  </div>
                )}
              </div>

              {/* Purchase Status & Actions */}
              {gift.purchased ? (
                <div className="flex items-center justify-center gap-2 py-3 bg-green-50 text-green-700 rounded-lg">
                  <Check className="h-4 w-4" />
                  <span className="font-medium">Already Purchased</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Store Link */}
                  {gift.storeUrl && (
                    <a
                      href={gift.storeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      Buy at {gift.store}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  
                  {/* Alternative Purchase Button */}
                  {!gift.storeUrl && (
                    <button
                      onClick={() => handlePurchaseGift(gift)}
                      className="w-full bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Gift className="h-4 w-4" />
                      Mark as Purchased
                    </button>
                  )}

                  {/* Wishlist Button */}
                  <button className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
                    <Heart className="h-4 w-4" />
                    Add to Wishlist
                  </button>
                </div>
              )}

              {/* Gift Notes */}
              {gift.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 italic">"{gift.notes}"</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredGifts.length === 0 && (
        <div className="text-center py-12">
          <Gift className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No gifts found</h3>
          <p className="text-gray-600">
            {filter === 'available' ? 'All gifts have been purchased!' :
             filter === 'purchased' ? 'No gifts have been purchased yet.' :
             'No gifts have been added to the registry yet.'}
          </p>
        </div>
      )}
    </div>
  );
};